import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";

export const useMetaAdsStatusUpdate = () => {
	const queryClient = useQueryClient();
	const company = useCompany();
	const emit = useAlertEmit();

	const updateCampaignStatus = useMutation({
		mutationFn: async ({
			campaignId,
			status,
		}: {
			campaignId: string;
			status: "ACTIVE" | "PAUSED";
		}) => {
			const result = await Api.UpdateMetaAdsCampaignStatus(campaignId, {
				status,
			});
			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					"COMPANY",
					company.id,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
				],
			});
			emit({ type: AlertType.SUCCESS, content: "Campaign status updated." });
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update campaign status.",
			});
		},
	});

	const updateAdSetStatus = useMutation({
		mutationFn: async ({
			adSetId,
			status,
		}: {
			adSetId: string;
			status: "ACTIVE" | "PAUSED";
		}) => {
			const result = await Api.UpdateMetaAdsAdSetStatus(adSetId, {
				status,
			});
			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					"COMPANY",
					company.id,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
				],
			});
			emit({ type: AlertType.SUCCESS, content: "Ad set status updated." });
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update ad set status.",
			});
		},
	});

	const updateAdStatus = useMutation({
		mutationFn: async ({
			adId,
			status,
		}: {
			adId: string;
			status: "ACTIVE" | "PAUSED";
		}) => {
			const result = await Api.UpdateMetaAdsAdStatus(adId, {
				status,
			});
			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					"COMPANY",
					company.id,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
				],
			});
			emit({ type: AlertType.SUCCESS, content: "Ad status updated." });
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to update ad status.",
			});
		},
	});

	return { updateCampaignStatus, updateAdSetStatus, updateAdStatus };
};
